import React, {Fragment, useState} from "react";
import {
    Button,
    useLocale,
    useRedirect,
    useTranslate
} from "react-admin";

import {ORDER_STATUS, PLANNED, PLANNED_ONE_TIME} from "../constants";
import {ROLE_USER} from "../../user/constants";
import {periodicityName} from "../../contract/constants";
import {getAddressTitle, Tooltip} from "../../../components";
import {diffInMinutes, prettyTimeString, timeString, totalMinutes} from "../../activity/utils";
import {partnerAddressAdditionalInfo} from "../../partner/utils";

import moment from 'moment'
import {stringify} from 'query-string';

import InfoIcon from '@mui/icons-material/Info';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ClickAwayListener from "@mui/material/ClickAwayListener";
import {makeStyles} from "@material-ui/core";
import {fullNamesListText} from "../../../components/FullNameField";
import {orderPrice} from "../../../data/utils";


const useStyles = makeStyles({
    event: {
        borderRadius: '3px',
        whiteSpace: 'initial',
        paddingLeft: '3px',
        margin: '1px',
    },
});


export const OrderContent = ({permissions, location, order, calendarApi}) => {
    const translate = useTranslate();
    const locale = useLocale();
    const redirect = useRedirect();
    const classes = useStyles();

    const [openTooltip, setOpenTooltip] = useState(false);

    const activitiesText = (activities, permissions) => {
        if (!activities) return '';
        const formattedActivities = permissions === ROLE_USER
            ? activities.map(a => `- ${a.name}`)
            : activities.map(a => `- ${a.shortName}`);
        return `<br>${formattedActivities.join('<br>')}`
    };

    const duty = order?.duty || order?.oneTimeDuty
    const isOneTimeOrder = !!order?.oneTimeDuty;
    const dutyAddress = order?.duty ? duty?.address?.address : duty?.address

    const detailButton = <Fragment>
        <Button
            onClick={() => redirect(`/qr?${stringify(
                {filter: JSON.stringify({addressId: dutyAddress?.id})}
            )}`, '', undefined, undefined, {fromButton: true})
            }
            label='app.message.detail_order_button'
        >
            <InfoIcon/>
        </Button>
    </Fragment>

    const oneTimeOrderTooltip = <div>
        {order?.helpers?.length > 0 &&
            <div>{translate('resources.orders.fields.alsoInvolved')}: {fullNamesListText(order?.helpers)}</div>}
        {permissions !== ROLE_USER &&
            <div>{translate('resources.orders.fields.oneTimeDuty.price')}: {duty?.price} €</div>}
        {permissions !== ROLE_USER && <div>{translate('resources.orders.fields.price')}: {orderPrice(order)} €</div>}
        <div>{translate('resources.contracts.fields.duties.name')}: {duty?.name}</div>
        <div>{translate('resources.orders.fields.code')}: {order.code}</div>
        <div>
            {translate('resources.orders.fields.oneTimeDuty.time')}: {new Date(order?.realizationDate).toLocaleTimeString(locale, {
            hour: 'numeric',
            minute: 'numeric'
        })}
        </div>
        <div>{translate('resources.orders.fields.duration')}: {prettyTimeString(order?.overrideMinutes)}</div>
        {permissions !== ROLE_USER && <div dangerouslySetInnerHTML={
            {__html: `${translate('resources.orders.fields.oneTimeDuty.note')}: ${duty?.note || ''}`}
        }/>}
        {order?.startRealizationDateTime && order?.endRealizationDateTime &&
            <div dangerouslySetInnerHTML={
                {__html: `${translate('resources.orders.fields.diffRealizationDateTime')}: ${prettyTimeString(diffInMinutes(order?.startRealizationDateTime, order?.endRealizationDateTime)) || ''}
                (${timeString(order?.startRealizationDateTime, locale)}-${timeString(order?.endRealizationDateTime, locale)})`}
            }/>
        }
        {order?.serviceReport &&
            <div dangerouslySetInnerHTML={
                {__html: `${translate('resources.orders.fields.serviceReport')}: ${order?.serviceReport || ''}`}
            }/>
        }
        {/*{permissions === ROLE_USER && detailButton}*/}
    </div>

    const orderTooltip = <div>
        {order?.helpers?.length > 0 &&
            <div>{translate('resources.orders.fields.alsoInvolved')}: {fullNamesListText(order?.helpers)}</div>}
        {permissions !== ROLE_USER && <div>{translate('resources.orders.fields.price')}: {orderPrice(order)} €</div>}
        <div>{translate('resources.contracts.fields.duties.periodicity')}: {periodicityName(translate, order.duty)}</div>
        <div>{translate('resources.contracts.fields.duties.name')}: {duty?.name}</div>
        <div>{translate('resources.orders.fields.code')}: {order.code}</div>
        <div>{partnerAddressAdditionalInfo(translate, duty?.address)}</div>
        <div dangerouslySetInnerHTML={
            {__html: `${translate('resources.contracts.fields.duties.activities.title')}: ${activitiesText(duty?.activities, permissions)}`}
        }/>
        {permissions !== ROLE_USER && order?.note &&
            <div dangerouslySetInnerHTML={
                {__html: `${translate('resources.orders.fields.note')}: ${order?.note || ''}`}
            }/>
        }
        {order?.startRealizationDateTime && order?.endRealizationDateTime &&
            <div dangerouslySetInnerHTML={
                {__html: `${translate('resources.orders.fields.diffRealizationDateTime')}: ${prettyTimeString(diffInMinutes(order?.startRealizationDateTime, order?.endRealizationDateTime)) || ''}
                (${timeString(order?.startRealizationDateTime, locale)}-${timeString(order?.endRealizationDateTime, locale)})`}
            }/>
        }
        {order?.serviceReport &&
            <div dangerouslySetInnerHTML={
                {__html: `${translate('resources.orders.fields.serviceReport')}: ${order?.serviceReport || ''}`}
            }/>
        }
        {permissions === ROLE_USER && moment(order?.realizationDate).isSame(moment(), 'day') && detailButton}
    </div>

    const eventDetails = isOneTimeOrder ? oneTimeOrderTooltip : orderTooltip;

    const OrderTitle = ({onClick}) => {
        const orderStatus = ORDER_STATUS.find(o => o.id === order?.status);
        const color = order?.status === PLANNED && order?.oneTimeDuty ? PLANNED_ONE_TIME.color : orderStatus?.color;
        const numberOfParticipants = (order?.helpers?.length || 0) + (order?.assignee ? 1 : 0);
        const minutesSpent = diffInMinutes(order?.startRealizationDateTime, order?.endRealizationDateTime) * numberOfParticipants;
        const expectedMinutes = totalMinutes(order?.duty?.activities);
        const bigDifferenceBetweenSpentAndExpected = minutesSpent ? (minutesSpent * 2 < expectedMinutes || minutesSpent > expectedMinutes * 2) : false;
        const missingQrScan = order?.startRealizationDateTimeFromQr === false || order?.endRealizationDateTimeFromQr === false

        return (
            <div
                className={classes.event}
                style={{
                    backgroundColor: color,
                    cursor: 'pointer',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    paddingLeft: '6px',
                    paddingRight: '6px',
                    width: '100%',
                    boxSizing: 'border-box',
                    overflow: 'hidden'
                }}
                onClick={onClick}
            >
                <span
                    style={{
                        whiteSpace: 'nowrap',
                        flexGrow: 1,
                        overflow: 'hidden'
                    }}
                >
            {getAddressTitle(dutyAddress)}
        </span>

                {(missingQrScan || bigDifferenceBetweenSpentAndExpected) && (
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginLeft: '6px',
                            flexShrink: 0
                        }}
                    >
                        {missingQrScan && (
                            <QrCodeScannerIcon
                                style={{
                                    fontSize: '14px',
                                    color: '#000000',
                                    marginBottom: bigDifferenceBetweenSpentAndExpected ? '2px' : '0px',
                                    backgroundImage: 'linear-gradient(-20deg, transparent 48%, #ff4d4d 48%, #ff4d4d 52%, transparent 52%)',
                                    borderRadius: '2px'
                                }}
                            />
                        )}

                        {bigDifferenceBetweenSpentAndExpected && (
                            <AccessTimeIcon
                                style={{
                                    fontSize: '14px',
                                    color: '#ff4d4d'
                                }}
                            />
                        )}
                    </div>
                )}
            </div>
        );
    };

    const clickEvent = <ClickAwayListener onClickAway={() => setOpenTooltip(false)}>
        <div>
            <Tooltip
                PopperProps={{sx: {zIndex: 1000}}}
                open={openTooltip}
                title={eventDetails}
            >
                <OrderTitle onClick={() => setOpenTooltip(true)}/>
            </Tooltip>
        </div>
    </ClickAwayListener>;

    const hoverEvent = <Tooltip title={eventDetails} disableInteractive>
        <OrderTitle onClick={() => redirect(`orders/${order?.id}${location}`)}/>
    </Tooltip>;

    return <div
        className='draggable-event'
        orderId={order?.id}
    >
        {permissions === ROLE_USER ? clickEvent : hoverEvent}
    </div>
};
